import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../../../../i18n';
import { useCurrentUser } from '../../../../../hooks/useCurrentUser';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { ConfirmDialog } from '../../../../shared/components/ConfirmDialog';
import {
  fetchPersonaCentros,
  desasignarPersonaCentro,
  type CentroAsignacionItem,
} from '../../../../shared/services/api';
import { cardStyle, formatDate } from './persona-detail-shared';
import { AssignToCentroModal } from './AssignToCentroModal';

export function PersonaCentrosTab({ personaId }: { personaId: string }) {
  const { t } = useI18n();
  const [centros, setCentros] = useState<CentroAsignacionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { isAdmin } = useCurrentUser();
  const [showAssignCentro, setShowAssignCentro] = useState(false);
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    title: string;
    message: string;
    danger?: boolean;
    onConfirm: () => void;
  } | null>(null);

  const loadCentros = useCallback(() => {
    fetchPersonaCentros(personaId)
      .then((r) => setCentros(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [personaId]);

  useEffect(() => {
    loadCentros();
  }, [loadCentros]);

  const handleRemove = (asignacionId: string) => {
    setConfirmState({
      open: true,
      title: t.common.confirmar,
      message: t.people.confirmDesasignarCentro,
      danger: true,
      onConfirm: async () => {
        await desasignarPersonaCentro(personaId, asignacionId);
        fetchPersonaCentros(personaId)
          .then((r) => setCentros(r.data))
          .catch(() => {});
      },
    });
  };

  if (loading)
    return (
      <div style={{ padding: 20 }}>
        <LoadingSpinner size="sm" muted />
      </div>
    );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {isAdmin && (
        <div>
          <button
            onClick={() => setShowAssignCentro(true)}
            style={{
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: 600,
              fontFamily: 'inherit',
              border: '1px solid #2F5496',
              borderRadius: 6,
              background: '#2F5496',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            + Asignar a centro
          </button>
        </div>
      )}
      {showAssignCentro && (
        <AssignToCentroModal
          personaId={personaId}
          onClose={() => setShowAssignCentro(false)}
          onAssigned={() => {
            setShowAssignCentro(false);
            loadCentros();
          }}
        />
      )}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--color-primario)' }}>
          Centros de Trabajo
        </h3>
        {centros.length === 0 ? (
          <div style={{ color: '#9CA3AF', fontSize: 13 }}>Sin centros asignados</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#0E243E' }}>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '14px 20px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                  }}
                >
                  Centro
                </th>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '14px 20px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                  }}
                >
                  Ciudad
                </th>
                <th
                  style={{
                    textAlign: 'center',
                    padding: '14px 20px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                  }}
                >
                  Principal
                </th>
                <th
                  style={{
                    textAlign: 'center',
                    padding: '14px 20px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                  }}
                >
                  Desde
                </th>
                <th style={{ padding: '14px 20px' }}></th>
              </tr>
            </thead>
            <tbody>
              {centros.map((pc) => (
                <tr key={pc.id} style={{ borderBottom: '1px solid #F2F4F6' }}>
                  <td style={{ padding: '14px 20px', fontWeight: 500, fontSize: 14 }}>
                    <div>{pc.centro?.nombre}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>{pc.centro?.codigo}</div>
                  </td>
                  <td style={{ padding: '14px 20px', fontSize: 14 }}>{pc.centro?.ciudad ?? '—'}</td>
                  <td style={{ padding: '14px 20px', textAlign: 'center', fontSize: 14 }}>
                    {pc.esPrincipal ? (
                      <span style={{ color: '#16A34A', fontWeight: 600 }}>Si</span>
                    ) : (
                      <span style={{ color: '#9CA3AF' }}>No</span>
                    )}
                  </td>
                  <td style={{ padding: '14px 20px', textAlign: 'center', fontSize: 14 }}>
                    {formatDate(pc.fechaInicio)}
                  </td>
                  <td style={{ padding: '14px 20px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleRemove(pc.id)}
                      style={{
                        padding: '4px 12px',
                        border: '1px solid #DC2626',
                        borderRadius: 4,
                        background: 'transparent',
                        color: '#DC2626',
                        cursor: 'pointer',
                        fontSize: 13,
                        fontFamily: 'inherit',
                      }}
                    >
                      Desasignar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {confirmState && (
        <ConfirmDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          danger={confirmState.danger}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
}
